/**
 * Modal for approving AI-composed outbound emails to external recipients.
 *
 * Fired when the backend emits an `email_review_required` SSE event — the
 * cloud `send_message` tool never auto-sends to non-PiPar addresses. The user
 * can edit the body/subject, then Send (calls /contacts/send_message) or
 * Discard.
 */

import { useState, useCallback } from 'preact/hooks'
import type { EmailReviewRequest } from '../types'
import { sendContactMessage } from '../services/api'

interface Props {
  review: EmailReviewRequest
  onClose: () => void
}

export function EmailReviewDialog({ review, onClose }: Props) {
  const [subject, setSubject] = useState(review.subject)
  const [body, setBody] = useState(review.body)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = useCallback(async () => {
    setSending(true)
    setError(null)
    try {
      await sendContactMessage({
        userDisplayName: review.sender_display_name || review.sender_pipar_email,
        userPiparEmail: review.sender_pipar_email,
        toEmail: review.to_email,
        subject,
        message: body,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send email')
      setSending(false)
    }
  }, [review, subject, body, onClose])

  const handleDiscard = useCallback(() => {
    if (sending) return
    onClose()
  }, [sending, onClose])

  const recipientLabel = review.recipient_display_name && review.recipient_display_name !== review.to_email
    ? `${review.recipient_display_name} <${review.to_email}>`
    : review.to_email

  return (
    <div style={overlayStyle} onClick={handleDiscard}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {'\u2709\uFE0F'} Email ready to send
            </div>
            <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
              Review and approve before sending. External address, so nothing is sent automatically.
            </div>
          </div>
          <button type="button" style={closeBtnStyle} onClick={handleDiscard} disabled={sending} aria-label="Close">
            {'\u2715'}
          </button>
        </div>

        <div style={contentAreaStyle}>
          <div style={fieldRowStyle}>
            <label style={labelStyle}>To</label>
            <div style={readOnlyValueStyle}>{recipientLabel}</div>
          </div>
          <div style={fieldRowStyle}>
            <label style={labelStyle}>From</label>
            <div style={readOnlyValueStyle}>{review.sender_pipar_email}</div>
          </div>
          <div style={fieldRowStyle}>
            <label style={labelStyle} htmlFor="email-review-subject">Subject</label>
            <input
              id="email-review-subject"
              type="text"
              value={subject}
              onInput={(e) => setSubject((e.currentTarget as HTMLInputElement).value)}
              style={inputStyle}
              disabled={sending}
              maxLength={200}
            />
          </div>
          <div style={fieldRowStyle}>
            <label style={labelStyle} htmlFor="email-review-body">Message</label>
            <textarea
              id="email-review-body"
              value={body}
              onInput={(e) => setBody((e.currentTarget as HTMLTextAreaElement).value)}
              style={textareaStyle}
              disabled={sending}
              rows={14}
            />
          </div>
          {error && <div style={errorStyle}>{error}</div>}
        </div>

        <div style={footerStyle}>
          <button type="button" style={discardBtnStyle} onClick={handleDiscard} disabled={sending}>
            Discard
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" style={sendBtnStyle} onClick={handleSend} disabled={sending || !subject.trim() || !body.trim()}>
            {sending ? 'Sending\u2026' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

const overlayStyle: Record<string, string | number> = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: Record<string, string | number> = {
  width: '90%',
  maxWidth: 640,
  maxHeight: '85vh',
  background: '#0a1f24',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 12,
  color: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyle: Record<string, string | number> = {
  padding: '16px 20px',
  borderBottom: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  flexShrink: 0,
}

const closeBtnStyle: Record<string, string | number> = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(250,250,250,0.5)',
  fontSize: 18,
  cursor: 'pointer',
  padding: '4px 8px',
}

const contentAreaStyle: Record<string, string | number> = {
  flex: 1,
  overflow: 'auto',
  padding: 20,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}

const fieldRowStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}

const labelStyle: Record<string, string | number> = {
  fontSize: 11,
  fontWeight: 500,
  color: 'rgba(250,250,250,0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const readOnlyValueStyle: Record<string, string | number> = {
  padding: '8px 10px',
  background: 'rgba(0,51,68,0.6)',
  borderRadius: 6,
  fontSize: 13,
  color: 'rgba(250,250,250,0.85)',
}

const inputStyle: Record<string, string | number> = {
  padding: '8px 10px',
  background: 'rgba(0,51,68,0.6)',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
}

const textareaStyle: Record<string, string | number> = {
  ...inputStyle,
  resize: 'vertical',
  lineHeight: '1.5',
  whiteSpace: 'pre-wrap',
  minHeight: 200,
}

const errorStyle: Record<string, string | number> = {
  padding: '8px 12px',
  background: 'rgba(255,68,68,0.1)',
  border: '1px solid rgba(255,100,100,0.3)',
  borderRadius: 6,
  color: '#ff6b6b',
  fontSize: 12,
}

const footerStyle: Record<string, string | number> = {
  padding: '12px 20px',
  borderTop: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  flexShrink: 0,
}

const discardBtnStyle: Record<string, string | number> = {
  padding: '8px 20px',
  border: '1px solid rgba(255,100,100,0.3)',
  borderRadius: 8,
  background: 'rgba(255,68,68,0.1)',
  color: '#ff6b6b',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
}

const sendBtnStyle: Record<string, string | number> = {
  padding: '8px 24px',
  border: 'none',
  borderRadius: 8,
  background: 'rgba(45,165,160,0.3)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
}
